/**
 * Menu de âncoras da página de produto:
 * - marca a aba da seção que está na tela;
 * - compensa a rolagem para o título da seção não ficar escondido atrás dos menus fixos.
 * A altura do cabeçalho (--header-height), usada para grudar logo abaixo dele, vem do
 * header.js: é ele quem também decide quando o cabeçalho "encaixa" (volta ao padrão) ao
 * encostar neste menu.
 */
(() => {
  const boot1 = (scope = document) =>
    scope.querySelectorAll('[data-section-tabs]').forEach((nav) => {
    if (nav.dataset.initialized) return;
    nav.dataset.initialized = 'true';

    const links = Array.from(nav.querySelectorAll('[data-tab-target]'));
    const entries = links
      .map((link) => ({ link, target: document.getElementById(link.dataset.tabTarget) }))
      .filter((entry) => entry.target);

    const offset = () => nav.offsetHeight + parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || 0) + 8;

    let activeEntry = null;

    // Enquanto a página ainda está rolando até o destino de um clique, a checagem por scroll
    // fica pausada — sem isso, ao clicar numa aba distante, o indicador de "aba ativa" passa
    // rapidamente pelas abas do caminho, e a cada troca a barra tenta se recentralizar na
    // aba daquele instante: a barra de abas fica pulando de um lado a outro enquanto a página
    // ainda rola, parecendo travamento (mais visível no celular, onde a barra ocupa mais espaço).
    // "scrollend" avisa quando a rolagem realmente parou; o tempo fixo é só uma rede de segurança
    // para navegadores que ainda não têm esse evento.
    let settling = false;
    let settleTimeout = null;
    const stopSettling = () => {
      settling = false;
      window.clearTimeout(settleTimeout);
    };
    if ('onscrollend' in window) window.addEventListener('scrollend', stopSettling);

    const setActive = (entry, { recenter } = {}) => {
      activeEntry = entry;
      links.forEach((link) => link.classList.toggle('is-active', entry && entry.link === link));
      if (entry && recenter) entry.link.scrollIntoView({ block: 'nearest', inline: 'center' });
    };

    entries.forEach((clicked) => {
      clicked.link.addEventListener('click', (event) => {
        event.preventDefault();
        const top = clicked.target.getBoundingClientRect().top + window.scrollY - offset();

        // Assume a aba clicada como ativa na hora: é a intenção do usuário, não precisa
        // esperar a rolagem chegar lá para refletir isso na barra.
        setActive(clicked, { recenter: true });
        settling = true;
        window.clearTimeout(settleTimeout);
        settleTimeout = window.setTimeout(stopSettling, 1500);

        window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      });
    });

    const update = () => {
      if (settling) return; // rolagem de um clique ainda em curso: não competir com ela

      // Calcula o deslocamento uma vez só por chamada, não a cada seção verificada.
      const currentOffset = offset();
      let current = null;
      entries.forEach((entry) => {
        if (entry.target.getBoundingClientRect().top - currentOffset <= 1) current = entry;
      });
      if (current === activeEntry) return; // nada mudou: evita mexer no DOM à toa
      setActive(current, { recenter: true });
    };

    // O evento de rolagem pode disparar dezenas de vezes por segundo; sem agrupar num único
    // quadro de animação, essa checagem (uma leitura de posição por seção do menu) roda a mais
    // do que a tela consegue desenhar e deixa a rolagem travando, principalmente rolando rápido.
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();
  });
  boot1();
  document.addEventListener('shopify:section:load', (event) => boot1(event.target));
})();
